import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import PageMeta from '../../../components/base/PageMeta';
import JsonLd from '../../../components/base/JsonLd';
import BrandLogo from '../../../components/base/BrandLogo';
import FloatingCtaBar from '../../../components/feature/FloatingCtaBar';
import SiteFooter from '../../../components/feature/SiteFooter';

const tools = [
  {
    name: 'KKday',
    slug: 'kkday',
    tagline: "Asia's #1 Experiences & Road Trip Activity Platform",
    desc: "KKday is Asia's leading online travel experiences marketplace offering vehicle rentals, self-drive tours, railway passes, and motorsport experiences across 100+ countries. Seamless booking for road trip enthusiasts.",
    tags: ['Road Trips', 'Vehicle Rental', 'Travel', 'Asia'],
    rating: 4.6,
    reviews: 18500,
    price: 'Activities from $5',
    priceType: 'accessories',
    link: 'https://kkday.sjv.io/2ano5A',
    logoIcon: 'ri-road-map-line',
    logoGrad: 'from-orange-500 to-amber-500',
    badge: 'Top Pick',
    badgeColor: 'bg-amber-100 text-amber-700',
    logoUrl: 'https://icon.horse/icon/kkday.com',
  },
  {
    name: 'Maxpeedingrods',
    slug: 'maxpeedingrods',
    tagline: 'High-Performance Car Parts & Racing Components',
    desc: 'Maxpeedingrods manufactures premium coilovers, turbochargers, connecting rods, and suspension components for performance-oriented drivers. Factory-direct pricing 30–50% below comparable premium brands.',
    tags: ['Performance Parts', 'Coilovers', 'Turbo', 'Racing'],
    rating: 4.5,
    reviews: 8920,
    price: 'Parts from $29.99',
    priceType: 'accessories',
    link: 'https://www.maxpeedingrods.com/?tracking=68186777bb122',
    logoIcon: 'ri-tools-line',
    logoGrad: 'from-red-600 to-rose-700',
    badge: 'Popular',
    badgeColor: 'bg-red-100 text-red-700',
    logoUrl: 'https://icon.horse/icon/maxpeedingrods.com',
  },
  {
    name: 'Kemimoto',
    slug: 'kemimoto',
    tagline: 'Premium ATV, UTV & Motorcycle Accessories',
    desc: 'Kemimoto offers high-quality accessories for ATVs, UTVs, motorcycles, and snowmobiles — windshields, covers, mirrors, mounts, and skid plates tested in real-world off-road conditions for genuine durability.',
    tags: ['ATV', 'UTV', 'Powersports', 'Off-Road'],
    rating: 4.6,
    reviews: 5640,
    price: 'Accessories from $15.99',
    priceType: 'accessories',
    link: 'https://kemimoto.sjv.io/9LV69W',
    logoIcon: 'ri-motorbike-line',
    logoGrad: 'from-slate-700 to-gray-800',
    badge: '',
    badgeColor: '',
    logoUrl: 'https://icon.horse/icon/kemimoto.com',
  },
  {
    name: 'Fapomoto',
    slug: 'fapomoto',
    tagline: 'Innovative Motorcycle Accessories & Riding Gear',
    desc: 'Fapomoto delivers rider-engineered motorcycle accessories including weatherproof phone mounts, handlebar bags, windshields, and comfort upgrades designed for real-world durability and tool-free installation.',
    tags: ['Motorcycle', 'Riding Gear', 'Mounts', 'Accessories'],
    rating: 4.4,
    reviews: 3180,
    price: 'From $12.99',
    priceType: 'accessories',
    link: 'https://www.fapomoto.com/?ref=lgzsytrb',
    logoIcon: 'ri-ride-line',
    logoGrad: 'from-zinc-700 to-slate-800',
    badge: '',
    badgeColor: '',
    logoUrl: 'https://icon.horse/icon/fapomoto.com',
  },
  {
    name: 'Carpuride',
    slug: 'carpuride',
    tagline: 'Smart Car Displays, Dashcams & In-Car Technology',
    desc: 'Carpuride brings wireless CarPlay and Android Auto to any vehicle with plug-and-play smart displays. No stereo replacement needed — just mount, connect, and enjoy factory-quality infotainment in minutes.',
    tags: ['CarPlay', 'Dashcam', 'Smart Display', 'Navigation'],
    rating: 4.5,
    reviews: 7350,
    price: 'From $49.99',
    priceType: 'accessories',
    link: 'https://carpuride.com/?ref=wzmoilfn',
    logoIcon: 'ri-dashboard-3-line',
    logoGrad: 'from-sky-600 to-cyan-700',
    badge: 'Trending',
    badgeColor: 'bg-sky-100 text-sky-700',
    logoUrl: 'https://icon.horse/icon/carpuride.com',
  },
  {
    name: 'Prismbeam',
    slug: 'prismbeam',
    tagline: 'Premium LED Automotive Lighting Upgrades',
    desc: 'Prismbeam LED headlight bulbs deliver 300% more brightness than factory halogens with a 50,000+ hour lifespan. Plug-and-play installation with engineered beam patterns that improve safety without blinding other drivers.',
    tags: ['LED Lighting', 'Headlights', 'Fog Lights', 'Safety'],
    rating: 4.7,
    reviews: 4290,
    price: 'From $39.99',
    priceType: 'accessories',
    link: 'https://prismbeam.com/?ref=HAONGUYEN',
    logoIcon: 'ri-lightbulb-flash-line',
    logoGrad: 'from-amber-500 to-yellow-500',
    badge: "Editor's Choice",
    badgeColor: 'bg-amber-100 text-amber-700',
    logoUrl: 'https://icon.horse/icon/prismbeam.com',
  },
  {
    name: 'Automobile Magazine',
    slug: 'automobile-magazine',
    tagline: "America's Premier Automotive Journalism & Car Reviews",
    desc: "Automobile Magazine delivers authoritative car reviews, comparison tests, and long-term ownership evaluations from veteran journalists since 1986. Essential reading for buyers and enthusiasts serious about vehicle decisions.",
    tags: ['Car Reviews', 'Automotive News', 'Journalism', 'Media'],
    rating: 4.5,
    reviews: 6840,
    price: 'Free · From $12/yr',
    priceType: 'media',
    link: 'https://www.automobilemag.com/',
    logoIcon: 'ri-newspaper-line',
    logoGrad: 'from-gray-700 to-slate-800',
    badge: '',
    badgeColor: '',
    logoUrl: 'https://icon.horse/icon/automobilemag.com',
  },
  {
    name: 'FreightWaves',
    slug: 'freightwave',
    tagline: 'Real-Time Freight Market Data & Logistics Intelligence',
    desc: 'FreightWaves delivers the industry-leading SONAR data platform with real-time freight market analytics alongside award-winning journalism covering trucking, intermodal, ocean freight, and supply chain technology.',
    tags: ['Freight', 'Logistics', 'Market Data', 'Trucking'],
    rating: 4.6,
    reviews: 3420,
    price: 'Free news · SONAR from $1,500/mo',
    priceType: 'media',
    link: 'https://www.freightwaves.com/',
    logoIcon: 'ri-truck-line',
    logoGrad: 'from-blue-700 to-indigo-800',
    badge: 'Industry Leader',
    badgeColor: 'bg-blue-100 text-blue-700',
    logoUrl: 'https://icon.horse/icon/freightwaves.com',
  },
  {
    name: 'Transport Topics',
    slug: 'transport-topics',
    tagline: 'The Trucking & Commercial Transportation Industry Authority',
    desc: 'Official ATA publication Transport Topics has delivered authoritative commercial transportation news, regulatory analysis, and industry rankings since 1935. Essential reading for fleet executives and logistics professionals.',
    tags: ['Trucking', 'Commercial Transport', 'Fleet', 'Industry News'],
    rating: 4.4,
    reviews: 2890,
    price: 'Free access',
    priceType: 'media',
    link: 'https://www.ttnews.com/',
    logoIcon: 'ri-file-list-3-line',
    logoGrad: 'from-slate-600 to-gray-700',
    badge: '',
    badgeColor: '',
    logoUrl: 'https://icon.horse/icon/ttnews.com',
  },
  {
    name: 'JOC',
    slug: 'joc',
    tagline: 'Global Trade, Shipping & Port Operations Intelligence',
    desc: "The Journal of Commerce is the world's leading intelligence platform for global trade and container shipping. With 190+ years of authority, JOC delivers real-time port data, ocean freight analytics, and expert market analysis.",
    tags: ['Shipping', 'Ocean Freight', 'Global Trade', 'Ports'],
    rating: 4.5,
    reviews: 4120,
    price: 'Free news · Data from $299/mo',
    priceType: 'media',
    link: 'https://www.joc.com/',
    logoIcon: 'ri-ship-2-line',
    logoGrad: 'from-cyan-700 to-teal-800',
    badge: '',
    badgeColor: '',
    logoUrl: 'https://icon.horse/icon/joc.com',
  },
  {
    name: 'CarTrade',
    slug: 'cartrade',
    tagline: "India's Largest Multi-Brand Used & New Car Marketplace",
    desc: "CarTrade is India's largest automotive marketplace connecting millions of buyers and sellers with AI-powered price valuation, inspection reports, loan pre-approval, and insurance — all in one seamless platform.",
    tags: ['Car Marketplace', 'Used Cars', 'India', 'Classifieds'],
    rating: 4.3,
    reviews: 9870,
    price: 'Free for buyers',
    priceType: 'marketplace',
    link: 'https://www.cartrade.com/',
    logoIcon: 'ri-car-line',
    logoGrad: 'from-emerald-600 to-teal-700',
    badge: '',
    badgeColor: '',
    logoUrl: 'https://icon.horse/icon/cartrade.com',
  },
  {
    name: 'Jiji Africa',
    slug: 'jiji-africa',
    tagline: "Africa's #1 Classifieds Marketplace for Vehicles & More",
    desc: "Jiji Africa is the continent's largest classifieds marketplace operating across Nigeria, Ghana, Kenya, Tanzania, Uganda, and Ethiopia. Tens of thousands of vehicle listings updated daily with direct chat for negotiation.",
    tags: ['Africa', 'Classifieds', 'Used Cars', 'Marketplace'],
    rating: 4.4,
    reviews: 7650,
    price: 'Free · Promoted from $5',
    priceType: 'marketplace',
    link: 'https://jiji.com.gh/',
    logoIcon: 'ri-global-line',
    logoGrad: 'from-orange-600 to-red-600',
    badge: 'Hot',
    badgeColor: 'bg-orange-100 text-orange-700',
    logoUrl: 'https://icon.horse/icon/jiji.com.gh',
  },
];

const relatedCategories = [
  {
    icon: 'ri-plane-line',
    grad: 'from-teal-400 to-cyan-500',
    name: 'Travel – Lifestyle Services',
    desc: 'Travel booking platforms, lifestyle services, and adventure planning tools',
    count: 96,
    href: '/categories/travel-lifestyle',
  },
  {
    icon: 'ri-robot-2-line',
    grad: 'from-violet-500 to-purple-600',
    name: 'Software – SaaS – AI',
    desc: 'Cloud software, AI-powered platforms and next-generation digital tools',
    count: 160,
    href: '/categories/software-saas-ai',
  },
  {
    icon: 'ri-building-2-line',
    grad: 'from-fuchsia-400 to-rose-500',
    name: 'Real Estate & Property',
    desc: 'Property listing tools, real estate management and housing platforms',
    count: 72,
    href: '/categories',
  },
];

type FilterTab = 'all' | 'accessories' | 'media' | 'marketplace';
type SortOption = 'highest_rating' | 'most_reviews' | 'default';

const filterLabels: Record<FilterTab, string> = {
  all: 'All',
  accessories: 'Parts & Accessories',
  media: 'Media & News',
  marketplace: 'Marketplace',
};

export default function AutomotiveTransportationPage() {
  const { i18n } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [showSignUp, setShowSignUp] = useState(false);
  const [signUpEmail, setSignUpEmail] = useState('');
  const [signUpPassword, setSignUpPassword] = useState('');
  const [signUpDone, setSignUpDone] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [filterTab, setFilterTab] = useState<FilterTab>('all');
  const [sortBy, setSortBy] = useState<SortOption>('highest_rating');
  const [sortOpen, setSortOpen] = useState(false);
  const langRef = useRef<HTMLDivElement>(null);
  const sortRef = useRef<HTMLDivElement>(null);

  const currentLang = i18n.language === 'vi' ? 'VI' : 'EN';

  const sortLabels: Record<SortOption, string> = {
    highest_rating: 'Highest rating',
    most_reviews: 'Most reviews',
    default: 'Default',
  };

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(e.target as Node)) setLangOpen(false);
      if (sortRef.current && !sortRef.current.contains(e.target as Node)) setSortOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filtered = tools
    .filter((t) => filterTab === 'all' || t.priceType === filterTab)
    .sort((a, b) => {
      if (sortBy === 'highest_rating') return b.rating - a.rating;
      if (sortBy === 'most_reviews') return b.reviews - a.reviews;
      return 0;
    });

  const renderStars = (rating: number) => {
    const full = Math.floor(rating);
    const half = rating - full >= 0.5;
    return Array.from({ length: 5 }).map((_, i) => {
      if (i < full) return <i key={i} className="ri-star-fill text-amber-400 text-xs"></i>;
      if (i === full && half) return <i key={i} className="ri-star-half-line text-amber-400 text-xs"></i>;
      return <i key={i} className="ri-star-line text-gray-300 text-xs"></i>;
    });
  };

  const siteUrl = import.meta.env.VITE_SITE_URL ?? 'https://example.com';

  const pageSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: 'Automotive & Transportation | 247digitrend',
    url: `${siteUrl}/categories/automotive-transportation`,
    description: 'Discover and compare the best automotive parts, in-car technology, transportation media, and vehicle marketplaces. 12 curated services with expert reviews.',
    isPartOf: { '@id': `${siteUrl}/#website` },
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
        { '@type': 'ListItem', position: 2, name: 'Categories', item: `${siteUrl}/categories` },
        { '@type': 'ListItem', position: 3, name: 'Automotive & Transportation', item: `${siteUrl}/categories/automotive-transportation` },
      ],
    },
  };

  return (
    <div className="min-h-screen" style={{ background: '#f8fafc', fontFamily: "'Inter', sans-serif" }}>
      <PageMeta
        title="Best Automotive & Transportation Services (2026) – Compare & Reviews | 247digitrend"
        description="Compare the best automotive parts, car accessories, in-car tech, and transportation platforms in 2026. Expert reviews of Maxpeedingrods, Carpuride, Prismbeam, FreightWaves, CarTrade and more."
        canonical={`${siteUrl}/categories/automotive-transportation`}
        keywords="best automotive accessories 2026, car parts review, Carpuride CarPlay review, Prismbeam LED headlights, freight logistics platforms, car marketplace"
      />
      <JsonLd data={pageSchema} />
      <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap" rel="stylesheet" />

      {/* SIGN UP MODAL */}
      {showSignUp && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={() => { setShowSignUp(false); setSignUpDone(false); }}>
          <div className="relative bg-white rounded-2xl w-full max-w-sm mx-4 p-8" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => { setShowSignUp(false); setSignUpDone(false); }} className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors cursor-pointer">
              <i className="ri-close-line text-gray-600 text-base"></i>
            </button>
            {signUpDone ? (
              <div className="text-center py-4">
                <div className="w-14 h-14 flex items-center justify-center mx-auto mb-4 rounded-full bg-slate-100">
                  <i className="ri-check-line text-slate-600 text-2xl"></i>
                </div>
                <h3 className="text-lg font-black text-gray-900 mb-2">Registration Successful!</h3>
                <p className="text-sm text-gray-500">Welcome to 247digitrend.</p>
                <button onClick={() => { setShowSignUp(false); setSignUpDone(false); }} className="mt-6 w-full bg-gradient-to-r from-slate-700 to-gray-800 text-white text-sm font-semibold py-3 rounded-full cursor-pointer hover:opacity-90 transition-opacity whitespace-nowrap">Close</button>
              </div>
            ) : (
              <>
                <div className="text-center mb-6">
                  <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3 rounded-xl bg-gradient-to-br from-slate-700 to-gray-800">
                    <i className="ri-car-line text-white text-base"></i>
                  </div>
                  <h3 className="text-xl font-black text-gray-900 mb-1">Create Account</h3>
                  <p className="text-xs text-gray-500">Join 50K+ users of 247digitrend</p>
                </div>
                <form onSubmit={(e) => { e.preventDefault(); setSignUpDone(true); }} className="space-y-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Email</label>
                    <input type="email" required value={signUpEmail} onChange={(e) => setSignUpEmail(e.target.value)} placeholder="your@email.com" className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-xl outline-none focus:border-slate-400 transition-colors" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Password</label>
                    <input type="password" required value={signUpPassword} onChange={(e) => setSignUpPassword(e.target.value)} placeholder="At least 8 characters" className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-xl outline-none focus:border-slate-400 transition-colors" />
                  </div>
                  <button type="submit" className="w-full bg-gradient-to-r from-slate-700 to-gray-800 text-white text-sm font-semibold py-3 rounded-full cursor-pointer hover:opacity-90 transition-opacity whitespace-nowrap mt-2">Sign Up Now</button>
                </form>
                <p className="text-xs text-center text-gray-400 mt-4">By signing up, you agree to our <a href="#" className="text-slate-600 underline">Terms</a>.</p>
              </>
            )}
          </div>
        </div>
      )}

      {/* HEADER */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white/95 backdrop-blur-sm border-b border-gray-100' : 'bg-white border-b border-gray-100'}`}>
        <div className="mx-auto px-8 py-2 flex items-center justify-between">
          <a href="/" className="flex items-center cursor-pointer">
            <img src="https://storage.readdy-site.link/project_files/3785ac0c-31ac-4f9e-b2ab-27ad3777db2a/7544398a-500f-41a6-81c3-aa7efbf1c0d2_logo-247.png?v=04135332188f9bc8b00780c66cd5837d" alt="247digitrend" className="h-36 w-auto object-contain" />
          </a>
          <nav className="hidden md:flex items-center gap-8">
            <a href="/" className="text-[15px] font-medium text-gray-700 hover:text-slate-800 transition-colors cursor-pointer">Home</a>
            <a href="/categories" className="text-[15px] font-semibold text-slate-800 transition-colors cursor-pointer">Categories</a>
            <a href="/reviews" className="text-[15px] font-medium text-gray-700 hover:text-slate-800 transition-colors cursor-pointer">Reviews</a>
            <a href="/comparisons" className="text-[15px] font-medium text-gray-700 hover:text-slate-800 transition-colors cursor-pointer">Comparisons</a>
            <a href="/blog" className="text-[15px] font-medium text-gray-700 hover:text-slate-800 transition-colors cursor-pointer">Blog</a>
            <button className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors cursor-pointer">
              <i className="ri-search-line text-gray-500 text-sm"></i>
            </button>
          </nav>
          <div className="flex items-center gap-3">
            <div className="relative" ref={langRef}>
              <button onClick={() => setLangOpen(!langOpen)} className="flex items-center gap-1.5 bg-gray-100 hover:bg-gray-200 transition-colors rounded-full px-3 py-1.5 cursor-pointer">
                <i className="ri-translate-2 text-xs text-gray-500"></i>
                <span className="text-xs font-semibold text-gray-700">{currentLang}</span>
                <i className={`ri-arrow-down-s-line text-xs text-gray-400 transition-transform ${langOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {langOpen && (
                <div className="absolute right-0 top-full mt-1.5 bg-white rounded-xl border border-gray-100 overflow-hidden w-32" style={{ boxShadow: '0 4px 16px rgba(0,0,0,0.08)' }}>
                  {(['VI', 'EN'] as const).map((lang) => (
                    <button key={lang} onClick={() => { i18n.changeLanguage(lang.toLowerCase()); setLangOpen(false); }} className={`w-full flex items-center gap-2 px-3 py-2 text-xs font-semibold transition-colors cursor-pointer ${currentLang === lang ? 'bg-slate-50 text-slate-700' : 'text-gray-600 hover:bg-gray-50'}`}>
                      <span>{lang === 'VI' ? '🇻🇳' : '🇺🇸'}</span>
                      <span>{lang === 'VI' ? 'Tiếng Việt' : 'English'}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button onClick={() => setShowSignUp(true)} className="bg-gradient-to-r from-slate-700 to-gray-800 text-white text-sm font-semibold px-5 py-2 rounded-full whitespace-nowrap cursor-pointer hover:opacity-90 transition-opacity">
              Sign Up
            </button>
          </div>
        </div>
      </header>

      {/* PAGE CONTENT */}
      <main className="pt-28 pb-16">
        <div className="max-w-5xl mx-auto px-6">

          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 mb-6">
            <a href="/" className="w-7 h-7 flex items-center justify-center rounded-lg bg-white border border-gray-200 hover:border-slate-300 transition-colors cursor-pointer">
              <i className="ri-home-4-line text-gray-500 text-xs"></i>
            </a>
            <i className="ri-arrow-right-s-line text-gray-400 text-sm"></i>
            <a href="/categories" className="w-7 h-7 flex items-center justify-center rounded-lg bg-white border border-gray-200 hover:border-slate-300 transition-colors cursor-pointer">
              <i className="ri-apps-2-line text-gray-500 text-xs"></i>
            </a>
          </nav>

          {/* Category Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-black text-gray-900 mb-2 tracking-tight">Automotive &amp; Transportation</h1>
            <p className="text-sm text-gray-500 mb-4">Car parts, accessories, in-car technology, logistics media, and vehicle marketplaces for drivers and industry professionals</p>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1.5 bg-white border border-gray-200 rounded-full px-3 py-1.5">
                <i className="ri-tools-line text-gray-500 text-xs"></i>
                <span className="text-xs font-semibold text-gray-700">{tools.length} services</span>
              </div>
              <div className="flex items-center gap-1.5 bg-slate-50 border border-slate-200 rounded-full px-3 py-1.5">
                <i className="ri-star-fill text-amber-500 text-xs"></i>
                <span className="text-xs font-semibold text-slate-700">Highly rated</span>
              </div>
            </div>
          </div>

          {/* Filter + Sort Bar */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center bg-white border border-gray-200 rounded-full px-1 py-1 gap-0.5">
              {(Object.keys(filterLabels) as FilterTab[]).map((tab) => (
                <button
                  key={tab}
                  onClick={() => setFilterTab(tab)}
                  className={`px-3.5 py-1.5 rounded-full text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
                    filterTab === tab
                      ? 'bg-gradient-to-r from-slate-700 to-gray-800 text-white'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {filterLabels[tab]}
                </button>
              ))}
            </div>

            <div className="relative" ref={sortRef}>
              <button
                onClick={() => setSortOpen(!sortOpen)}
                className="flex items-center gap-2 bg-white border border-gray-200 rounded-full px-4 py-2 text-xs font-semibold text-gray-700 hover:border-gray-300 transition-colors cursor-pointer whitespace-nowrap"
              >
                <span>Sort by: {sortLabels[sortBy]}</span>
                <i className={`ri-arrow-down-s-line text-gray-400 transition-transform ${sortOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {sortOpen && (
                <div className="absolute right-0 top-full mt-1.5 bg-white rounded-xl border border-gray-100 overflow-hidden w-44 z-10" style={{ boxShadow: '0 4px 16px rgba(0,0,0,0.1)' }}>
                  {(Object.entries(sortLabels) as [SortOption, string][]).map(([key, label]) => (
                    <button key={key} onClick={() => { setSortBy(key); setSortOpen(false); }} className={`w-full flex items-center gap-2 px-4 py-2.5 text-xs font-semibold transition-colors cursor-pointer text-left ${sortBy === key ? 'bg-slate-50 text-slate-700' : 'text-gray-600 hover:bg-gray-50'}`}>
                      {sortBy === key && <i className="ri-check-line text-slate-600"></i>}
                      <span className={sortBy === key ? '' : 'ml-4'}>{label}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Tools List */}
          <div className="space-y-3">
            {filtered.map((tool, i) => (
              <div key={i} className="bg-white rounded-2xl border border-gray-200 p-5 hover:border-slate-300 transition-all group">
                <div className="flex items-start gap-4">
                  <BrandLogo logoUrl={tool.logoUrl} logoGrad={tool.logoGrad} logoIcon={tool.logoIcon} name={tool.name} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                          <h3 className="text-base font-bold text-gray-900">{tool.name}</h3>
                          {tool.badge && (
                            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${tool.badgeColor}`}>{tool.badge}</span>
                          )}
                        </div>
                        <p className="text-xs font-medium text-slate-600 mb-1.5">{tool.tagline}</p>
                        <p className="text-xs text-gray-500 leading-relaxed line-clamp-2 mb-3">{tool.desc}</p>
                        <div className="flex flex-wrap gap-1.5 mb-3">
                          {tool.tags.map((tag, ti) => (
                            <span key={ti} className="text-xs font-medium text-gray-500 bg-gray-100 rounded-full px-2.5 py-0.5">{tag}</span>
                          ))}
                        </div>
                        <p className="text-xs text-gray-500">
                          <i className="ri-price-tag-3-line mr-1 text-gray-400"></i>
                          {tool.price}
                        </p>
                      </div>
                      <div className="flex flex-col items-end gap-3 flex-shrink-0">
                        <div className="text-right">
                          <div className="flex items-center gap-1 justify-end mb-0.5">
                            <div className="flex items-center gap-0.5">{renderStars(tool.rating)}</div>
                            <span className="text-sm font-bold text-gray-800 ml-0.5">{tool.rating}</span>
                          </div>
                          <span className="text-xs text-gray-400">({tool.reviews.toLocaleString()} reviews)</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <a
                            href={`/categories/automotive-transportation/${tool.slug}`}
                            className="flex items-center gap-1.5 px-3.5 py-2 border border-gray-300 text-gray-600 hover:border-slate-400 hover:text-slate-700 text-xs font-semibold rounded-lg transition-all cursor-pointer whitespace-nowrap"
                          >
                            View Details
                          </a>
                          <a
                            href={tool.link}
                            target="_blank"
                            rel="noopener noreferrer nofollow"
                            className="flex items-center gap-1.5 px-3.5 py-2 bg-slate-700 hover:bg-slate-800 text-white text-xs font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                          >
                            <i className="ri-external-link-line text-xs"></i>
                            Access
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}

            {filtered.length === 0 && (
              <div className="text-center py-16 bg-white rounded-2xl border border-gray-200">
                <div className="w-14 h-14 flex items-center justify-center mx-auto mb-4 rounded-full bg-gray-100">
                  <i className="ri-inbox-line text-2xl text-gray-400"></i>
                </div>
                <p className="text-sm font-semibold text-gray-500">No services found</p>
                <p className="text-xs text-gray-400 mt-1">Try a different filter</p>
              </div>
            )}
          </div>

          {/* RELATED CATEGORIES */}
          <div className="mt-12">
            <h2 className="text-lg font-bold text-gray-900 mb-5">Related categories</h2>
            <div className="grid grid-cols-3 gap-4">
              {relatedCategories.map((cat, i) => (
                <a key={i} href={cat.href} className="bg-white rounded-2xl border border-gray-200 p-5 hover:border-slate-300 transition-all cursor-pointer group">
                  <div className={`w-10 h-10 flex items-center justify-center rounded-xl bg-gradient-to-br ${cat.grad} mb-3`}>
                    <i className={`${cat.icon} text-white text-lg`}></i>
                  </div>
                  <h3 className="text-sm font-bold text-gray-900 mb-1 group-hover:text-slate-700 transition-colors">{cat.name}</h3>
                  <p className="text-xs text-gray-500 leading-relaxed mb-3">{cat.desc}</p>
                  <div className="flex items-center gap-1 text-xs text-gray-400">
                    <i className="ri-tools-line"></i>
                    <span>{cat.count} tools</span>
                  </div>
                </a>
              ))}
            </div>
          </div>
        </div>
      </main>

      {/* ── FOOTER ── */}
      <SiteFooter />

      <FloatingCtaBar onSignUp={() => setShowSignUp(true)} />
    </div>
  );
}
